import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Shield, Package, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import StokSekme from '../../components/StokSekme';
import StokTablosu from '../../components/StokTablosu';
import StokAktarModal from '../../components/StokAktarModal';
import { ProgressIndicator, formatCurrency } from '../../components/StockIndicator';
import IzolasyonTable from './IzolasyonTable';
import IzolasyonModal from './IzolasyonModal';
import UygulamaModal from './UygulamaModal';
import { izolasyonTipleri, getIzolasyonByInsaat } from '@mocks/materials/izolasyon';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import { getHareketler, hareketTipleri } from '@mocks/materials/stokHareketleri';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Izolasyon() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [activeTab, setActiveTab] = useState('kullanim');
    const [filterTip, setFilterTip] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isUygulamaOpen, setIsUygulamaOpen] = useState(false);
    const [isAktarOpen, setIsAktarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getIzolasyonByInsaat(insaatId);
        if (filterTip) data = data.filter(i => i.tip === filterTip);
        return data;
    }, [insaatId, filterTip]);

    const stats = useMemo(() => {
        const toplamMetrekare = filteredData.reduce((acc, s) => acc + s.miktar, 0);
        const uygulananMetrekare = filteredData.reduce((acc, s) => acc + s.uygulananMiktar, 0);
        const kalanStok = filteredData.filter(s => s.durum === 'teslim').reduce((acc, s) => acc + s.kalanMiktar, 0);
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        return { toplamMetrekare, uygulananMetrekare, kalanStok, toplamMaliyet };
    }, [filteredData]);

    const stokHavuzuData = getStokHavuzu('izolasyon');
    const hareketlerData = getHareketler('izolasyon');

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };
    const handleUpdateProgress = (item) => { setSelectedItem(item); setIsUygulamaOpen(true); };

    const handleSubmit = async (fd, isEdit) => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 800));

        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            addMockCariHareket(fd.supplierId, insaatId || 'c1', {
                type: 'purchase',
                subtype: 'fatura',
                amount: fd.kdvDahilToplam,
                date: fd.siparisTarihi || new Date().toISOString().split('T')[0],
                description: `İzolasyon Alımı — ${fd.miktar} ${fd.birim} ${fd.tip}`,
            });
        }

        toast.success(isEdit ? 'İzolasyon güncellendi' : 'Yeni izolasyon eklendi');
        setIsLoading(false);
        setIsModalOpen(false);

        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            setOdemePlanData({ amount: fd.kdvDahilToplam, companyName: fd.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };

    const handleOdemePlanConfirm = ({ nextDueDate }) => {
        toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`);
        setIsOdemePlanOpen(false);
        setOdemePlanData(null);
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 500));
        toast.success('İzolasyon silindi');
        setIsLoading(false);
        setIsDeleteOpen(false);
    };

    const handleUygulamaUpdate = async (uygulamaData) => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success(`Uygulama güncellendi: %${uygulamaData.uygulamaYuzde}`); setIsLoading(false); setIsUygulamaOpen(false); };
    const handleAktarim = async (d) => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success(`${d.miktar} m² stok aktarıldı`); setIsLoading(false); setIsAktarOpen(false); };

    const statsCards = [
        { title: 'Toplam Sipariş', value: `${stats.toplamMetrekare} m²`, icon: Package, color: 'primary' },
        { title: 'Uygulanan', value: `${stats.uygulananMetrekare} m²`, icon: CheckCircle, color: 'success' },
        { title: 'Stok', value: `${stats.kalanStok} m²`, icon: Shield, color: 'info' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'warning' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'tip', label: 'Tip' },
        { key: 'marka', label: 'Marka' },
        { key: 'model', label: 'Model' },
        { key: 'miktar', label: 'Miktar', type: 'number' },
        { key: 'uygulamaYuzde', label: 'Uygulama %' },
        { key: 'kalanMiktar', label: 'Stok', type: 'number' },
        { key: 'toplamFiyat', label: 'Tutar', type: 'currency' },
        { key: 'uygulamaDurumu', label: 'Durum', type: 'status' },
        { key: 'uygulamaAlani', label: 'Uygulama Alanı', fullWidth: true },
    ];

    const headerFilter = (
        <div className="flex items-center gap-3">
            <StokSekme activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'kullanim' && (
                <select value={filterTip} onChange={(e) => setFilterTip(e.target.value)} className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]">
                    <option value="">Tüm Tipler</option>
                    {izolasyonTipleri.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
            )}
        </div>
    );

    return (
        <>
            <MaterialPageLayout title="İzolasyon" subtitle="Kaba İnşaat" icon={Shield} iconBgColor="bg-success" backLink="/insaat/kaba" stats={statsCards} onAddClick={activeTab === 'kullanim' ? handleAdd : undefined} headerFilter={headerFilter}>
                {activeTab === 'kullanim' ? (
                    <>
                        <div className="p-4 border-b border-border-light bg-bg-tertiary/50">
                            <ProgressIndicator completed={stats.uygulananMetrekare} total={stats.toplamMetrekare} label="Uygulama İlerlemesi" />
                        </div>
                        <IzolasyonTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} onUpdateProgress={handleUpdateProgress} />
                    </>
                ) : (
                    <StokTablosu stokData={stokHavuzuData} hareketData={hareketlerData} hareketTipleri={hareketTipleri} birim="m²" onAktarim={() => setIsAktarOpen(true)} />
                )}
            </MaterialPageLayout>

            <IzolasyonModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="İzolasyon Detayı" />
            <UygulamaModal isOpen={isUygulamaOpen} onClose={() => setIsUygulamaOpen(false)} onSubmit={handleUygulamaUpdate} data={selectedItem} isLoading={isLoading} />
            <StokAktarModal isOpen={isAktarOpen} onClose={() => setIsAktarOpen(false)} onSubmit={handleAktarim} malzemeTipi="izolasyon" stokData={stokHavuzuData} birim="m²" isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="İzolasyonu Sil" message={`${selectedItem?.marka} - ${selectedItem?.model} izolasyonunu silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal
                isOpen={isOdemePlanOpen}
                onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }}
                onConfirm={handleOdemePlanConfirm}
                amount={odemePlanData?.amount}
                companyName={odemePlanData?.companyName}
            />
        </>
    );
}
